// Builds a complete, self-contained HTML order email (no SendGrid template).
// Used by the submission, shipped, and accepted/declined order flows so all
// three share one unbranded, white-label layout. Table-based with all-inline
// styles for broad email-client compatibility (Gmail, Outlook).
//
// `data` is the same field bag previously passed as `dynamic_template_data`,
// plus the pre-built inner line-item table HTML in `data.lineItems`.

const fontStack = "Arial, Helvetica, sans-serif";

const styles = {
  body: `margin:0;padding:0;background-color:#f4f5f6;`,
  outerTable: `width:100%;background-color:#f4f5f6;`,
  card: `width:600px;max-width:600px;margin:0 auto;background-color:#ffffff;border:1px solid #e4e6e8;border-radius:8px;`,
  cardPad: `padding:32px;`,
  brand: `font-family:${fontStack};font-size:24px;font-weight:700;letter-spacing:0.12em;color:#222222;text-transform:uppercase;margin:0;`,
  divider: `border:none;border-top:1px solid #e4e6e8;margin:20px 0;`,
  greeting: `font-family:${fontStack};font-size:16px;font-weight:600;color:#222222;margin:0 0 12px 0;`,
  disposition: `font-family:${fontStack};font-size:14px;line-height:1.5;color:#333333;margin:0 0 24px 0;`,
  sectionTitle: `font-family:${fontStack};font-size:13px;font-weight:700;color:#222222;margin:0 0 10px 0;letter-spacing:0.02em;text-transform:uppercase;`,
  summaryTable: `width:100%;border-collapse:collapse;margin:0 0 24px 0;font-family:${fontStack};font-size:14px;line-height:1.4;color:#333333;`,
  summaryLabel: `padding:8px 0;color:#666666;vertical-align:top;width:45%;`,
  summaryValue: `padding:8px 0;color:#222222;font-weight:600;vertical-align:top;text-align:right;`,
  summaryValueLeft: `padding:8px 0;color:#222222;font-weight:600;vertical-align:top;`,
  footer: `font-family:${fontStack};font-size:12px;line-height:1.5;color:#999999;margin:24px 0 0 0;text-align:center;`,
};

function esc(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escMultiline(value) {
  return esc(value).replace(/\n/g, "<br />");
}

function isEmpty(value) {
  const text = String(value ?? "").trim();
  return text === "" || text === "-";
}

// Savings is passed as an HTML span; treat a zero amount as nothing to show.
function isZeroAmount(value) {
  const text = String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .trim();
  return text === "" || /^\$?0(\.0{1,2})?$/.test(text);
}

function financialRow(label, valueHtml, { emphasize = false } = {}) {
  const valueStyle = emphasize
    ? `${styles.summaryValue}font-size:16px;`
    : styles.summaryValue;
  return `<tr>
              <td style="${styles.summaryLabel}">${esc(label)}</td>
              <td style="${valueStyle}">${valueHtml}</td>
            </tr>`;
}

function detailRow(label, valueHtml) {
  return `<tr>
              <td style="${styles.summaryLabel}">${esc(label)}</td>
              <td style="${styles.summaryValueLeft}">${valueHtml}</td>
            </tr>`;
}

export function buildOrderEmailHtml(data = {}) {
  const notes = data.notes ?? data.additionalNotes;

  const financialRows = [
    isEmpty(data.subtotal) ? "" : financialRow("Subtotal", esc(data.subtotal)),
    isEmpty(data.shipping) ? "" : financialRow("Shipping", esc(data.shipping)),
    isZeroAmount(data.savings) ? "" : financialRow("Savings", data.savings),
    isEmpty(data.totalSum) ? "" : financialRow("Total", esc(data.totalSum)),
    isEmpty(data.balance)
      ? ""
      : financialRow("Balance Due", esc(data.balance), { emphasize: true }),
  ].join("");

  const detailRows = [
    isEmpty(data.paymentTerm)
      ? ""
      : detailRow("Payment Term", esc(data.paymentTerm)),
    isEmpty(data.totalQty)
      ? ""
      : detailRow("Total Quantity", esc(data.totalQty)),
    isEmpty(data.shipDate) ? "" : detailRow("Ship Date", esc(data.shipDate)),
    isEmpty(data.storeName) ? "" : detailRow("Account", esc(data.storeName)),
    isEmpty(data.storeAddress)
      ? ""
      : detailRow("Shipping Address", esc(data.storeAddress)),
    isEmpty(data.licenseNumber)
      ? ""
      : detailRow("License #", esc(data.licenseNumber)),
    isEmpty(data.licenseExpiration)
      ? ""
      : detailRow("License Expiration", esc(data.licenseExpiration)),
    isEmpty(data.deliveryPreferences)
      ? ""
      : detailRow(
          "Delivery Preferences",
          escMultiline(data.deliveryPreferences)
        ),
    isEmpty(notes) ? "" : detailRow("Notes", escMultiline(notes)),
  ].join("");

  const financialSection = financialRows
    ? `<p style="${styles.sectionTitle}">Order Summary</p>
            <table role="presentation" cellpadding="0" cellspacing="0" style="${styles.summaryTable}">
              <tbody>${financialRows}</tbody>
            </table>`
    : "";

  const detailSection = detailRows
    ? `<p style="${styles.sectionTitle}">Order Details</p>
            <table role="presentation" cellpadding="0" cellspacing="0" style="${styles.summaryTable}">
              <tbody>${detailRows}</tbody>
            </table>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <title>${esc(data.subject ?? "Order Update")}</title>
  </head>
  <body style="${styles.body}">
    <table role="presentation" cellpadding="0" cellspacing="0" style="${
      styles.outerTable
    }">
      <tr>
        <td align="center" style="padding:24px 12px;">
          <table role="presentation" cellpadding="0" cellspacing="0" style="${
            styles.card
          }">
            <tr>
              <td style="${styles.cardPad}">
                <p style="${styles.brand}">${esc(data.brand)}</p>
                <hr style="${styles.divider}" />
                <p style="${styles.greeting}">Hi ${esc(data.name)},</p>
                <p style="${styles.disposition}">${data.disposition ?? ""}</p>
                ${data.lineItems ?? ""}
                ${financialSection}
                ${detailSection}
                <p style="${
                  styles.footer
                }">This is an automated order notification. Please do not reply directly to this message.</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
}
